import React, { Fragment, useMemo, useState } from 'react';
import { EventRegistrationFormField } from 'src/generated/api/dsv-public/model';
import { Checkbox } from 'src/lib/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from 'src/lib/components/ui/radio-group';
import { useFormContext } from 'src/context/form-context';
import { useFormProgressStore } from 'src/stores/form-progress-store';
import { Label } from 'src/lib/components/ui/label';
import { CheckedState } from '@radix-ui/react-checkbox';
import { useTranslation } from 'react-i18next';

type TermsAndConditionsFieldProps = {
  field: EventRegistrationFormField;
  participantIndex: number;
};

export function TermsAndConditionsField({ field, participantIndex }: TermsAndConditionsFieldProps) {
  const { t } = useTranslation();
  const [touched, setTouched] = useState<boolean>(false);
  const { updateFieldValue } = useFormContext();
  const registrationEntries = useFormProgressStore(state => state.registrationEntries);
  const forceErrorMessages = useFormProgressStore(state => state.forceErrorMessages);
  const currentValue = useMemo(() => {
    if (registrationEntries && registrationEntries.length > participantIndex) {
      return registrationEntries[participantIndex].fields[field.key]
        ? String(registrationEntries[participantIndex].fields[field.key])
        : undefined;
    }

    return undefined;
  }, [field.key, registrationEntries, participantIndex]);

  const handleCheckboxChange = (checked: CheckedState) => {
    setTouched(true);
    updateFieldValue(participantIndex, field.key, checked ? field.options?.url : '');
  };

  const handleRadioChange = (value: string) => {
    setTouched(true);
    updateFieldValue(participantIndex, field.key, value);
  };

  const invalidFields = useFormProgressStore(state => state.invalidFields);
  const error = invalidFields.find(invF => invF.key === `${participantIndex}-${field.key}`);
  const showError = (touched || forceErrorMessages) && error;

  const checkboxLabel = field.options?.['checkbox-label'];

  function renderCheckboxLabelContent() {
    if (checkboxLabel) {
      return (
        <span>
          {checkboxLabel.split(/({{titleAndUrl}})/g).map((text, idx) => {
            if (text === '{{titleAndUrl}}') {
              return (
                <a key={idx} href={field.options?.url} target="_blank" rel="noreferrer" className="underline">
                  {field.name}
                </a>
              );
            } else {
              return <Fragment key={idx}>{text}</Fragment>;
            }
          })}
        </span>
      );
    }
    return (
      <span>
        {t('field.terms-and-conditions.accept-the')}{' '}
        <a href={field.options?.url} className="underline" target="_blank" rel="noreferrer">
          {field.name}
        </a>
        .
      </span>
    );
  }

  if (field.options?.['type'] === 'RADIO') {
    return (
      <div className="space-y-2" key={field.key}>
        <RadioGroup
          id={`${participantIndex}-${field.key}`}
          value={currentValue ?? ''}
          onValueChange={handleRadioChange}
          className="gap-2"
        >
          <div className="flex items-start gap-2">
            <RadioGroupItem
              id={`${participantIndex}-${field.key}-yes`}
              value={field.options?.url ?? 'true'}
              className="mt-0.5 flex-shrink-0"
            />
            <Label
              htmlFor={`${participantIndex}-${field.key}-yes`}
              className="text-sm leading-relaxed cursor-pointer flex-1"
            >
              <span className="font-medium">{t('field.terms-and-conditions.yes')}. </span>
              {renderCheckboxLabelContent()}
              {field.required && <span className="text-destructive">*</span>}
            </Label>
          </div>
          <div className="flex items-start gap-2">
            <RadioGroupItem id={`${participantIndex}-${field.key}-no`} value="false" className="mt-0.5 flex-shrink-0" />
            <Label
              htmlFor={`${participantIndex}-${field.key}-no`}
              className="text-sm leading-relaxed cursor-pointer flex-1"
            >
              {t('field.terms-and-conditions.no')}.
            </Label>
          </div>
        </RadioGroup>
        {showError && <p className="text-sm text-destructive">{error.message}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-2" key={field.key}>
      <div className="flex items-start gap-2" id={`${participantIndex}-${field.key}`}>
        <Checkbox
          id={`${participantIndex}-${field.key}-checkbox`}
          checked={currentValue === field.options?.url}
          onCheckedChange={handleCheckboxChange}
          className="mt-0.5 flex-shrink-0"
        />
        <Label
          htmlFor={`${participantIndex}-${field.key}-checkbox`}
          className="text-sm leading-relaxed cursor-pointer flex-1"
        >
          {renderCheckboxLabelContent()}
          {field.required && <span className="text-destructive">*</span>}
        </Label>
      </div>
      {showError && <p className="text-sm text-destructive">{error.message}</p>}
    </div>
  );
}
